import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';

// Create / edit a distributor or a store (the party master data that invoices
// are attached to). Distributors also carry a credit period (creditDays) that
// is used as the default due date of their purchase invoices.
function FinancePartyFormModal({ config, party, onClose, onSaved }) {
  const isEdit = Boolean(party && party.id);
  const [form, setForm] = useState({ name: '', contactPerson: '', phone: '', email: '', address: '', notes: '', creditDays: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setForm({
        name: party.name || '',
        contactPerson: party.contactPerson || '',
        phone: party.phone || '',
        email: party.email || '',
        address: party.address || '',
        notes: party.notes || '',
        creditDays: party.creditDays ? String(party.creditDays) : '',
      });
    } else {
      setForm({
        name: '',
        contactPerson: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
        creditDays: config.hasCreditDays ? '30' : '',
      });
    }
  }, [party, isEdit, config.hasCreditDays]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
    setServerError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = config.partyTitle + ' name is required';
    if (config.hasCreditDays && String(form.creditDays).trim() !== '') {
      const days = Number(form.creditDays);
      if (!Number.isInteger(days) || days < 0 || days > 365) {
        nextErrors.creditDays = 'Enter a whole number of days between 0 and 365';
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = { ...form };
    if (config.hasCreditDays) {
      const creditDaysRaw = String(form.creditDays).trim();
      payload.creditDays = creditDaysRaw === '' ? null : Number(creditDaysRaw);
    } else {
      delete payload.creditDays;
    }

    setSaving(true);
    setServerError('');
    try {
      const url = API_BASE_URL + '/api/admin/finance/' + config.partyEndpoint + (isEdit ? '/' + party.id : '');
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setServerError(data.message || 'Could not save the ' + config.partyTitle.toLowerCase());
        setSaving(false);
        return;
      }
      onSaved(config.partyTitle + (isEdit ? ' updated successfully' : ' added successfully'), data.data);
    } catch (error) {
      setServerError('Connection error: ' + error.message);
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: spacing.md,
    border: '2px solid ' + colors.border,
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };
  const labelStyle = {
    display: 'block',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  };
  const errorStyle = { color: colors.error, fontSize: '0.8rem', marginTop: spacing.xs, marginBottom: 0 };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 65,
        padding: spacing.md,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(event) => event.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows.xl,
          width: '100%',
          maxWidth: '560px',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: spacing.lg,
            borderBottom: '2px solid ' + colors.border,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: colors.text.primary }}>
            {(isEdit ? 'Edit ' : 'Add ') + config.partyTitle}
          </h3>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer', color: colors.text.secondary }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: spacing.lg }}>
          <div className='pd-form-grid'>
            <div>
              <label style={labelStyle} htmlFor='pd-party-form-name'>
                {config.partyTitle} Name *
              </label>
              <input
                id='pd-party-form-name'
                type='text'
                value={form.name}
                onChange={(event) => setField('name', event.target.value)}
                placeholder={'e.g. ' + (config.key === 'purchases' ? 'ABC Medical Distributors' : 'XYZ Medical Store')}
                style={inputStyle}
              />
              {errors.name ? <p style={errorStyle}>{errors.name}</p> : null}
            </div>

            <div>
              <label style={labelStyle} htmlFor='pd-party-form-contact'>
                Contact Person
              </label>
              <input
                id='pd-party-form-contact'
                type='text'
                value={form.contactPerson}
                onChange={(event) => setField('contactPerson', event.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle} htmlFor='pd-party-form-phone'>
                Phone Number
              </label>
              <input
                id='pd-party-form-phone'
                type='tel'
                value={form.phone}
                onChange={(event) => setField('phone', event.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle} htmlFor='pd-party-form-email'>
                Email
              </label>
              <input
                id='pd-party-form-email'
                type='email'
                value={form.email}
                onChange={(event) => setField('email', event.target.value)}
                style={inputStyle}
              />
            </div>

            {config.hasCreditDays ? (
              <div>
                <label style={labelStyle} htmlFor='pd-party-form-credit-days'>
                  Credit Days (Due Date Default)
                </label>
                <input
                  id='pd-party-form-credit-days'
                  type='number'
                  min='0'
                  max='365'
                  step='1'
                  value={form.creditDays}
                  onChange={(event) => setField('creditDays', event.target.value)}
                  placeholder='e.g. 30'
                  style={inputStyle}
                />
                {errors.creditDays ? <p style={errorStyle}>{errors.creditDays}</p> : null}
                <p style={{ color: colors.text.secondary, fontSize: '0.78rem', marginTop: spacing.xs, marginBottom: 0 }}>
                  New purchase invoices for this distributor get a due date of purchase date + these days (e.g. 30 or 45).
                  You can still change it on each invoice. Leave empty for no default.
                </p>
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: spacing.md }}>
            <label style={labelStyle} htmlFor='pd-party-form-address'>
              Address
            </label>
            <textarea
              id='pd-party-form-address'
              value={form.address}
              onChange={(event) => setField('address', event.target.value)}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginTop: spacing.md }}>
            <label style={labelStyle} htmlFor='pd-party-form-notes'>
              Notes
            </label>
            <textarea
              id='pd-party-form-notes'
              value={form.notes}
              onChange={(event) => setField('notes', event.target.value)}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {serverError ? (
            <div
              style={{
                marginTop: spacing.md,
                padding: spacing.md,
                borderRadius: '0.5rem',
                backgroundColor: '#fdeaea',
                color: '#b42318',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {serverError}
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.md, marginTop: spacing.lg, flexWrap: 'wrap' }}>
            <button
              type='button'
              onClick={onClose}
              disabled={saving}
              style={{
                padding: spacing.md + ' ' + spacing.lg,
                borderRadius: '0.5rem',
                border: '2px solid ' + colors.border,
                backgroundColor: 'white',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                color: colors.text.primary,
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={saving}
              style={{
                padding: spacing.md + ' ' + spacing.lg,
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: saving ? colors.neutral[400] : colors.primary[500],
                color: 'white',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add ' + config.partyTitle)}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default FinancePartyFormModal;


